import Phaser from 'phaser';
import { SceneKeys } from '../game/Constants';
import { AssetRegistry } from '../systems/AssetRegistry';
import { PlayerPreferences } from '../systems/PlayerPreferences';
import { TUTORIAL_STEPS, TutorialManager } from '../systems/TutorialManager';
import { VisualTheme, addPanel } from '../ui/VisualTheme';
import { createTextButton } from '../ui/components/UIButton';

interface TutorialSceneData { returnScene?: string; auto?: boolean; }

export class TutorialScene extends Phaser.Scene {
  private returnScene?: string;

  constructor() {
    super(SceneKeys.Tutorial);
  }

  init(data: TutorialSceneData) {
    this.returnScene = data.returnScene;
  }

  create() {
    this.render();
    this.input.keyboard?.on('keydown-LEFT', () => { TutorialManager.instance.previous(); this.render(); });
    this.input.keyboard?.on('keydown-RIGHT', () => { TutorialManager.instance.next(); this.render(); });
    this.input.keyboard?.on('keydown-ESC', () => this.close());
  }

  private render() {
    this.children.removeAll();
    const { width, height } = this.scale;
    const preferences = PlayerPreferences.instance;
    const manager = TutorialManager.instance;
    const step = manager.current;
    const index = TUTORIAL_STEPS.findIndex((entry) => entry.id === step.id);
    const artKey = AssetRegistry.instance.image(step.artAsset, 'title_screen');
    const safeKey = this.textures.exists(artKey) ? artKey : AssetRegistry.instance.image('title_screen', 'fallback.title');

    this.add.rectangle(width / 2, height / 2, width, height, VisualTheme.colors.ink, 0.97);
    this.add.image(width / 2, 265, safeKey).setDisplaySize(width, 530).setAlpha(0.64);
    this.add.rectangle(width / 2, 265, width, 530, VisualTheme.colors.ink, 0.34);
    addPanel(this, width / 2, 610, 1180, 500, { fill: preferences.value.highContrast ? 0x05080c : VisualTheme.colors.night, alpha: 0.98, stroke: VisualTheme.colors.goldBright });

    this.add.text(245, 400, `GUIDED CAMPAIGN  •  ${index + 1} / ${TUTORIAL_STEPS.length}`, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: `${preferences.fontSize(15)}px`,
      color: VisualTheme.text.accent,
      letterSpacing: 1,
    });
    this.add.text(245, 438, step.title, {
      fontFamily: VisualTheme.fonts.display,
      fontSize: `${preferences.fontSize(38)}px`,
      color: VisualTheme.text.title,
      stroke: '#120c08',
      strokeThickness: 3,
    });
    this.add.text(245, 500, step.body, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: `${preferences.fontSize(20)}px`,
      color: VisualTheme.text.body,
      wordWrap: { width: 1110 },
      lineSpacing: 7,
    });
    this.add.rectangle(width / 2, 646, 1080, 1, VisualTheme.colors.goldBright, 0.28);
    this.add.text(245, 674, 'CONTROLS & PRACTICE', {
      fontFamily: VisualTheme.fonts.display,
      fontSize: `${preferences.fontSize(18)}px`,
      color: VisualTheme.text.accent,
    });
    this.add.text(245, 710, step.controls, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: `${preferences.fontSize(17)}px`,
      color: VisualTheme.text.body,
      wordWrap: { width: 1100 },
      lineSpacing: 5,
    });

    for (let stepIndex = 0; stepIndex < TUTORIAL_STEPS.length; stepIndex++) {
      const completed = manager.state.completedSteps.includes(TUTORIAL_STEPS[stepIndex].id);
      const active = stepIndex === index;
      this.add.circle(500 + stepIndex * 50, 820, active ? 8 : 6, active ? VisualTheme.colors.goldBright : completed ? preferences.semanticColor('success') : VisualTheme.colors.slate, 1)
        .setStrokeStyle(1, VisualTheme.colors.parchment, active ? 1 : 0.35);
    }

    createTextButton(this, { label: 'Previous', x: 220, y: 842, width: 190, onClick: () => { manager.previous(); this.render(); } });
    createTextButton(this, { label: index === TUTORIAL_STEPS.length - 1 ? 'Finish Guide' : 'Next', x: 1380, y: 842, width: 190, onClick: () => {
      if (index === TUTORIAL_STEPS.length - 1) { manager.next(); this.close(); }
      else { manager.next(); this.render(); }
    } });
    createTextButton(this, { label: 'Close', x: 800, y: 842, width: 190, onClick: () => this.close() });
    createTextButton(this, { label: 'Do Not Auto-Open', x: 1140, y: 842, width: 240, fontSize: 17, onClick: () => { manager.dismiss(); this.close(); } });

    const exposed = window as unknown as { render_game_to_text?: () => string };
    exposed.render_game_to_text = () => JSON.stringify({ mode: 'tutorial', step: step.id, index, total: TUTORIAL_STEPS.length });
  }

  private close() {
    this.scene.stop(SceneKeys.Tutorial);
    if (this.returnScene && this.scene.isPaused(this.returnScene)) this.scene.resume(this.returnScene);
  }
}
